import { useEffect, useState, useCallback } from "react";
import { extractApiPath, getFromDBandFile, setToDB } from "src/utils/cache/idbUtility";
import { getFromMemory } from "src/utils/cache/memoryCache";

const CACHE_DELAY_MS = 30; //to let react render the page 

export function useCache<T>(
  fullKey: string
): [T | null, React.Dispatch<React.SetStateAction<T | null>>, boolean] {
  const key = extractApiPath(fullKey);

  const [state, setState] = useState<T | null>(null);
  const [isCacheLoaded, setIsCacheLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;

    const memoryValue = getFromMemory<T>(key);
    if (memoryValue !== undefined && memoryValue !== null) {
      timeoutId = window.setTimeout(() => {
        if (!cancelled) {
          setState(memoryValue);
          setIsCacheLoaded(true);
        }
      }, CACHE_DELAY_MS);

      return () => {
        cancelled = true;
        if (timeoutId !== null) window.clearTimeout(timeoutId);
      };
    }

    const fetchFromCache = async () => {
      try {
        const cachedValue = await getFromDBandFile<T>(key);

        if (cancelled) return;

        if (cachedValue !== undefined && cachedValue !== null) {
          setState(cachedValue);
        }
      } catch (error) {
        console.error("Failed to load cache for key:", key, error);
      } finally {
        if (!cancelled) {
          setIsCacheLoaded(true);
        }
      }
    };

    setIsCacheLoaded(false);
    fetchFromCache();

    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [key]);

  const setCachedValue: React.Dispatch<React.SetStateAction<T | null>> =
    useCallback(
      (valueOrUpdater) => {
        setState((prev) => {
          const next =
            typeof valueOrUpdater === "function"
              ? (valueOrUpdater as (p: T | null) => T | null)(prev)
              : valueOrUpdater;

          setToDB(key, next as T);

          return next;
        });
      },
      [key]
    );

  return [state, setCachedValue, isCacheLoaded];
}

export default useCache;